"use client";

import React, { useState } from 'react';
import { toast } from 'sonner';

export default function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;

    // Simulate API call
    setTimeout(() => {
      toast.success(`Dziękujemy, ${name.split(' ')[0]}!`, {
        description: "Otrzymaliśmy Twoje zapytanie. Skontaktujemy się w ciągu 24 godzin.",
        duration: 6000,
      });

      // Reset form
      (e.target as HTMLFormElement).reset();
      setIsSubmitting(false);
    }, 650);
  };

  return (
    <section id="kontakt" className="section-band border-t">
      <div className="max-w-screen-2xl mx-auto px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-x-16 gap-y-12">
          {/* Form */}
          <div>
            <div className="text-blue-500 font-semibold tracking-widest text-sm mb-2">ZACZNIJMY WSPÓŁPRACĘ</div>
            <h2 className="text-4xl font-semibold tracking-tighter mb-2">Zapytaj o bezpłatną wycenę</h2>
            <p className="text-zinc-400 mb-8">Wyślij nam zdjęcia obiektu lub wypełnij formularz — przygotujemy indywidualną ofertę w ciągu 24 godzin.</p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-sm text-zinc-400 block mb-1.5">Imię i nazwisko</label>
                  <input 
                    type="text" 
                    name="name" 
                    required 
                    className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-2xl px-4 py-3 text-sm" 
                  />
                </div>
                <div>
                  <label className="text-sm text-zinc-400 block mb-1.5">Numer telefonu</label>
                  <input 
                    type="tel" 
                    name="phone" 
                    required 
                    className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-2xl px-4 py-3 text-sm" 
                  />
                </div>
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1.5">Adres e-mail</label>
                <input 
                  type="email" 
                  name="email" 
                  required 
                  className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-2xl px-4 py-3 text-sm" 
                />
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1.5">Rodzaj zapytania</label>
                <select 
                  name="type" 
                  className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-2xl px-4 py-3 text-sm"
                  defaultValue="Nowy system CCTV"
                >
                  <option value="Nowy system CCTV">Nowy system CCTV</option>
                  <option value="Modernizacja istniejącego systemu">Modernizacja istniejącego systemu</option>
                  <option value="Serwis i konserwacja">Serwis i konserwacja</option>
                  <option value="Monitoring zdalny">Monitoring zdalny / VMS</option>
                  <option value="Inne">Inne</option>
                </select>
              </div>

              <div>
                <label className="text-sm text-zinc-400 block mb-1.5">Wiadomość</label>
                <textarea 
                  name="message" 
                  rows={4} 
                  required 
                  className="w-full bg-zinc-950 border border-zinc-700 focus:border-blue-500 rounded-3xl px-4 py-3 text-sm resize-y min-h-[120px]" 
                  placeholder="Opisz krótko swój obiekt i oczekiwania..."
                />
              </div>

              <button 
                type="submit" 
                disabled={isSubmitting}
                className="w-full flex items-center justify-center gap-x-2 bg-blue-600 hover:bg-blue-500 active:bg-blue-700 disabled:opacity-70 disabled:cursor-not-allowed transition-all text-white font-semibold py-4 rounded-3xl text-base mt-2"
              >
                {isSubmitting ? "Wysyłanie..." : "Wyślij zapytanie"}
                {!isSubmitting && <span>↗</span>}
              </button>
              
              <p className="text-[11px] text-center text-zinc-500">Twoje dane są bezpieczne. Odpowiadamy w ciągu 24h.</p>
            </form>
          </div>

          {/* Contact Info */}
          <div className="lg:pt-8">
            <div className="dark-card rounded-3xl p-8">
              <h4 className="font-semibold text-lg mb-6">Dane kontaktowe</h4>
              
              <div className="space-y-5 text-sm">
                <div className="flex gap-4">
                  <div className="text-blue-400 w-5 mt-0.5">☎</div>
                  <div>
                    <a href="tel:+48661709414" className="font-medium hover:text-blue-400 transition-colors">+48 661 709 414</a>
                    <div className="text-zinc-400 text-xs">Infolinia czynna 8:00–18:00</div>
                  </div>
                </div>
                
                <div className="flex gap-4">
                  <div className="text-blue-400 w-5 mt-0.5">✉</div>
                  <div>
                    <a href="mailto:wojciet12@gmail.com" className="font-medium hover:text-blue-400 transition-colors">wojciet12@gmail.com</a>
                    <div className="text-zinc-400 text-xs">Odpowiadamy w ciągu 1 godziny roboczej</div>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="text-blue-400 w-5 mt-0.5">📍</div>
                  <div>
                    <div className="font-medium">ul. Piątkowska 8</div>
                    <div className="text-zinc-400">99-300 Kutno, Polska</div>
                  </div>
                </div>
              </div>

              <div className="mt-8 pt-7 border-t border-zinc-700 text-sm">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-emerald-400">⏰</span>
                  <span className="font-medium">Godziny pracy</span>
                </div>
                <div className="grid grid-cols-2 gap-x-4 text-xs text-zinc-400">
                  <div>Poniedziałek – Piątek</div>
                  <div className="text-right">08:00 – 18:00</div>
                  <div>Serwis 24/7</div>
                  <div className="text-right text-emerald-400">Zawsze dostępni</div>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="mt-6 flex flex-wrap gap-3 text-xs">
              <div className="px-4 py-2 border border-zinc-700 rounded-2xl flex items-center gap-x-2 text-zinc-400">
                <span>🛡️</span>
                <span>ISO 27001</span>
              </div>
              <div className="px-4 py-2 border border-zinc-700 rounded-2xl flex items-center gap-x-2 text-zinc-400">
                <span>📜</span>
                <span>Certyfikat UDT</span>
              </div>
            </div>

            {/* Mapa siedziby */}
            <div className="mt-6 dark-card rounded-3xl overflow-hidden">
              <iframe
                title="DaHaka Inc. – ul. Piątkowska 8, Kutno"
                src="https://maps.google.com/maps?q=ul.+Pi%C4%85tkowska+8,+99-300+Kutno&t=&z=15&ie=UTF8&iwloc=&output=embed"
                className="w-full h-56 border-0 grayscale invert opacity-80"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="px-4 py-3 text-xs text-zinc-400 border-t border-zinc-800">
                📍 ul. Piątkowska 8, 99-300 Kutno
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
